import {
  CanvasActionTypes,
  selectToEditBlock,
} from '@storeOld/reducers/canvas/canvas'
import { useDispatch, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import type { AppState } from '@/types'
import { allowEventTarget } from '@/utils/allowEventTarget'
import { Keys } from '../keyboard'

const useOnEnterKeyboardActions = () => {
  const dispatch = useDispatch()

  // node selectors
  const selectedNode = useSelector<AppState, string | null>(
    ({ canvas }) =>
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1]
  )

  const selectedToEditNode = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedToEditNode
  )

  const selectedNodeLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedNodeLink
  )

  // block selectors
  const selectedBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.ids[canvas.block.ids.length - 1]
  )

  const selectedToEditBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.selectedToEditBlockId
  )

  // symbol selectors
  const selectedSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedSymbol
  )

  const selectedToEditSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedToEditSymbol
  )

  // sticky note selectors
  const selectedStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedStickyNote
  )

  const selectedToEditStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedToEditStickyNote
  )

  // section selectors
  const selectedSection = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.section.selectedSectionId
  )

  const selectedToEditSection = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.section.selectedSectionEditingId
  )

  const { symbolLibrary } = useArtboardMode()

  // color picker
  const colorPickerIsOpen = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.colorPickerIsOpen
  )

  const onEnterNode = (event: KeyboardEvent) => {
    event.preventDefault()

    dispatch({
      type: CanvasActionTypes.SET_EDIT_NODE,
      payload: selectedNode,
    })
  }

  const onEnterBlock = (event: KeyboardEvent) => {
    event.preventDefault()

    // @todo implement action
    // @ts-expect-error
    dispatch(selectToEditBlock(selectedBlock as string))
  }

  const onEnterSymbol = (event: KeyboardEvent) => {
    event.preventDefault()

    dispatch({
      type: CanvasActionTypes.SELECT_TO_EDIT_SYMBOL,
      payload: selectedSymbol,
    })
  }

  const onEnterStickyNote = (event: KeyboardEvent) => {
    event.preventDefault()

    dispatch({
      type: CanvasActionTypes.SELECT_TO_EDIT_STICKY_NOTE,
      payload: selectedStickyNote,
    })
  }

  const onEnterSection = (event: KeyboardEvent) => {
    event.preventDefault()

    if (!selectedSection) {
      return
    }

    dispatch({
      type: CanvasActionTypes.SELECT_TO_EDIT_SECTION,
      payload: selectedSection,
    })
  }

  return (event: KeyboardEvent) => {
    if (event.code !== Keys.Enter) {
      return
    }

    // Enter typed into a field — the structure sidebar's search box, a block's
    // text — belongs to that field. The structure sidebar is a permanent panel,
    // so its mere presence must not disable Enter on the canvas; only focus
    // sitting in an editable target does.
    if (!allowEventTarget(event)) {
      return
    }

    if (
      selectedNode &&
      !selectedToEditNode &&
      !selectedNodeLink &&
      !colorPickerIsOpen
    ) {
      onEnterNode(event)
    }

    if (
      selectedBlock &&
      !selectedToEditBlock &&
      !symbolLibrary &&
      !colorPickerIsOpen
    ) {
      onEnterBlock(event)
    }

    if (
      selectedSymbol &&
      !selectedToEditSymbol &&
      !symbolLibrary &&
      !colorPickerIsOpen
    ) {
      onEnterSymbol(event)
    }

    if (selectedStickyNote && !selectedToEditStickyNote && !colorPickerIsOpen) {
      onEnterStickyNote(event)
    }

    if (selectedSection && !selectedToEditSection && !colorPickerIsOpen) {
      onEnterSection(event)
    }
  }
}

export default useOnEnterKeyboardActions
